import { useState } from 'react';
import { cn } from '@/lib/utils';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useProjectSearch } from '@/hooks/useCashFlow';
import type { CashFlowRequestOptions } from '@/lib/cashFlow';

/** The project a picker selection writes back (code + display name move together). */
export interface ProjectValue {
  project: string | null;
  projectName: string | null;
}

/** Cap the picker list so a blank query can't dump every project at once. */
const PROJECT_SEARCH_LIMIT = 8;

/**
 * Searchable Projekt picker (module 1.3): pick/search an EXISTING project — never
 * free text — so a reclassification can only attach a project the data already
 * knows. The distinct projects come from the shared query layer
 * (`useProjectSearch`, the same source both lenses' detail uses — L14); this
 * component owns no list of its own. The search panel is a CHILD mounted only
 * while the picker is open, so the query never runs (and needs no client) until
 * the user actually opens it.
 */
export function ProjectSelect({
  value,
  onChange,
  requestOptions,
}: {
  value: ProjectValue;
  onChange: (next: ProjectValue) => void;
  requestOptions?: CashFlowRequestOptions;
}) {
  const [open, setOpen] = useState(false);
  const selectedLabel = value.projectName ?? value.project;
  const hasSelection = selectedLabel !== null;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between rounded border border-border bg-background px-2 py-1.5 text-[12px] hover:border-[hsl(var(--primary))]/50"
      >
        <span className={cn('truncate', hasSelection ? 'text-foreground' : 'text-muted-foreground/60')}>
          {hasSelection ? selectedLabel : 'Projekt määramata'}
        </span>
        <span aria-hidden className="ml-2 shrink-0 text-muted-foreground">
          {open ? '▴' : '▾'}
        </span>
      </button>
      {open && (
        <ProjectSearchPanel
          hasSelection={hasSelection}
          requestOptions={requestOptions}
          onClear={() => {
            onChange({ project: null, projectName: null });
            setOpen(false);
          }}
          onPick={(next) => {
            onChange(next);
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}

/**
 * The open picker panel: a debounced search box over the distinct projects, with
 * an explicit "remove project" affordance when one is set. Mounted only while the
 * picker is open, so the search query is inert until the user opens it.
 */
function ProjectSearchPanel({
  hasSelection,
  onPick,
  onClear,
  requestOptions,
}: {
  hasSelection: boolean;
  onPick: (next: ProjectValue) => void;
  onClear: () => void;
  requestOptions?: CashFlowRequestOptions;
}) {
  const [query, setQuery] = useState('');
  const debounced = useDebouncedValue(query, 200);
  const { data, isLoading, isError } = useProjectSearch(debounced, {
    ...requestOptions,
    limit: PROJECT_SEARCH_LIMIT,
  });
  const results = data ?? [];

  return (
    <div className="absolute z-20 mt-1 w-full overflow-hidden rounded border border-border bg-popover shadow-lg">
      <div className="border-b border-border/70 p-1.5">
        <input
          autoFocus
          aria-label="Otsi projekti"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Otsi projekti…"
          className="w-full rounded border border-border bg-background px-2 py-1 text-[12px] text-foreground outline-none focus:border-[hsl(var(--primary))]/50"
        />
      </div>
      {hasSelection && (
        <button
          type="button"
          onClick={onClear}
          className="flex w-full items-center gap-1.5 border-b border-border/70 px-2 py-1.5 text-left text-[11px] text-muted-foreground hover:bg-accent"
        >
          <span aria-hidden>×</span> Eemalda projekt
        </button>
      )}
      <div className="max-h-48 overflow-auto">
        {isLoading ? (
          <p className="px-2 py-2 text-[11px] text-muted-foreground/70">Otsin…</p>
        ) : isError ? (
          <p className="px-2 py-2 text-[11px] text-[hsl(var(--cost))]">Otsing ebaõnnestus</p>
        ) : results.length === 0 ? (
          <p className="px-2 py-2 text-[11px] text-muted-foreground/70">Projekte ei leitud</p>
        ) : (
          <ul>
            {results.map((p) => (
              <li key={p.project}>
                <button
                  type="button"
                  onClick={() => onPick({ project: p.project, projectName: p.projectName })}
                  className="flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left hover:bg-accent"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-[12px] text-foreground">{p.projectName ?? p.project}</span>
                    {p.projectName && (
                      <span className="block truncate font-mono text-[10px] text-muted-foreground/70">{p.project}</span>
                    )}
                  </span>
                  <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground/60">{p.entryCount}×</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
